import { useMemo } from 'react';

import type { PatchedPlainRoute } from 'js/lib/connectToRouter';
import useRouter, { useParams } from 'js/lib/useRouter';

export interface HeartbeatPayload {
  routeKey: string;
  institutionSlug?: string;
  programSlug?: string;
  courseSlug?: string;
  contextId?: string;
  itemId?: string;
}

const stripInitialSlash = (urlPathSegment: string): string =>
  urlPathSegment[0] === '/' ? urlPathSegment.substring(1) : urlPathSegment;

const assertParamsAreNotArrays = (...args: Array<undefined | string | string[]>): void => {
  args.forEach((arg) => {
    if (Array.isArray(arg)) {
      throw new Error('Heartbeat system does not support duplicate identical parameters');
    }
  });
};

export const useHeartbeatPayload = (): HeartbeatPayload => {
  const { institutionSlug, programSlug, courseSlug, contextId, itemId } = useParams();
  assertParamsAreNotArrays(institutionSlug, programSlug, courseSlug, contextId, itemId);

  /*
    When we need to upgrade to the newest version of react-router, the below logic will fail for two reasons:
      - useRouter is no longer available
      - routes no longer have names
    
    At the time of writing, my recommendation would be to:

      1. Write a codemod to convert every instance of name in a route to "id", which is still accepted
      2. Convert the below logic to use [useMatches](https://reactrouter.com/en/main/hooks/use-matches) instead.
  */
  const { routes } = useRouter();

  const routeKey = routes.reduce((combinedPathString, newRoute: PatchedPlainRoute) => {
    const segment = newRoute.name || stripInitialSlash(newRoute.path || '');
    if (!segment) {
      return combinedPathString;
    }

    if (!combinedPathString) {
      return segment;
    }

    return `${combinedPathString}|${segment}`;
  }, '');

  return useMemo(
    () => ({
      routeKey,
      institutionSlug,
      programSlug,
      courseSlug,
      contextId,
      itemId,
    }),
    [contextId, courseSlug, institutionSlug, itemId, programSlug, routeKey]
  );
};
